<script>
    import { getContext, setContext } from 'svelte';
    import { derived, writable } from 'svelte/store';

    // --- Properties
    export let databaseKey;
    const { retrieve, aql, db } = getContext(databaseKey);

    const corr_highpass = writable(0);
    const matches_highpass = writable(1);

    // --- Retrieve matches and ordering
    const matches = retrieve(
        aql`
        FOR m in jb_cc2ly
            SORT m.percentage_matches DESC, m.correlation DESC
            RETURN m`,
        (res) =>
            res.map((m) => {
                return {
                    percentage_matches: m.percentage_matches,
                    percentage_price: m.percentage_price,
                    correlation: m.correlation,
                    num_matches: m.num_matches,
                    ccnum: m.cc,
                    lynum: m.ly,
                    _key: m._key,
                };
            })
    );
    const selected_matches = writable([]);
    $: $selected_matches = $matches.filter(
        (m) => m.correlation >= $corr_highpass && m.num_matches >= $matches_highpass
    );

    const orderCC = derived(selected_matches, ($selected_matches) => [
        ...new Set($selected_matches.map((d) => d.ccnum)),
    ]);
    const orderLY = derived(selected_matches, ($selected_matches) => [
        ...new Set($selected_matches.map((d) => d.lynum)),
    ]);

    // --- Loyaltycards
    const loyaltycards_ = retrieve(
        aql`
        FOR l in jb_loyaltycards
            return l
    `
    );

    const loyaltycards = writable({});
    $: updateLoyaltycards($selected_matches, $loyaltycards_);

    function updateLoyaltycards(matches, cards) {
        const lyccnum = matches.reduce((agg, m) => {
            const previous = m.lynum in agg ? agg[m.lynum] : [];
            if (!previous.includes(m.ccnum)) {
                agg[m.lynum] = [...previous, m.ccnum];
            }
            return agg;
        }, {});
        $loyaltycards = cards.reduce((agg, l) => {
            agg[l.lynum] = { ...l, ccnums: lyccnum[l.lynum] || [] };
            return agg;
        }, {});
    }

    // --- Creditcards
    const creditcards_ = retrieve(
        aql`
        FOR l in jb_creditcards
            return l
    `
    );
    const creditcards = writable({});
    $: updateCreditcards($selected_matches, $creditcards_);

    function updateCreditcards(matches, cards) {
        const cclynum = matches.reduce((agg, m) => {
            const previous = m.ccnum in agg ? agg[m.ccnum] : [];
            if (!previous.includes(m.lynum)) {
                agg[m.ccnum] = [...previous, m.lynum];
            }
            return agg;
        }, {});
        $creditcards = cards.reduce((agg, l) => {
            agg[l.ccnum] = { ...l, lynums: cclynum[l.ccnum] || [] };
            return agg;
        }, {});
    }

    // -- Rejected matches
    const rejectedMatches = writable([]);
    function restoreMatch(m) {
        $matches = [...$matches, m];
        $rejectedMatches = $rejectedMatches.filter((match) => match !== m);
        $creditcards[m.ccnum].lynums.push(m.lynum);
        $loyaltycards[m.lynum].ccnums.push(m.ccnum);
    }

    function rejectMatch(m) {
        $rejectedMatches = [...$rejectedMatches, m];
        $matches = $matches.filter((match) => match !== m);
        $creditcards[m.ccnum].lynums = $creditcards[m.ccnum].lynums.filter(
            (ly) => ly != m.lynum
        );
        $loyaltycards[m.lynum].ccnums = $loyaltycards[m.lynum].ccnums.filter(
            (cc) => cc != m.ccnum
        );
    }

    // --- Walk over connections in the network
    function _traverseCC(state, cc) {
        state.ccs[cc.ccnum] = cc;
        cc.lynums.forEach((lynum) => {
            if (!(lynum in state.lys)) {
                state = _traverseLY(state, $loyaltycards[lynum]);
            }
        });
        return state;
    }

    function _traverseLY(state, ly) {
        state.lys[ly.lynum] = ly;
        ly.ccnums.forEach((ccnum) => {
            if (!(ccnum in state.ccs)) {
                state = _traverseCC(state, $creditcards[ccnum]);
            }
        });
        return state;
    }

    // --- Hover state
    const hover = writable({
        ccs: {},
        lys: {},
        ccnum: null,
        lynum: null,
        match: null,
    });

    function hoverMatch(match) {
        const state = _traverseCC(
            { ccs: {}, lys: {} },
            $creditcards[match.ccnum]
        );
        state.match = match._key;
        state.ccnum = null;
        state.lynum = null;
        $hover = state;
    }

    function unhoverMatch(match) {
        if ($hover.match === match._key) {
            $hover = {
                ccs: {},
                lys: {},
                ccnum: null,
                lynum: null,
                match: null,
            };
        }
    }

    function hoverCC(cc) {
        const state = _traverseCC({ ccs: {}, lys: {} }, cc);
        state.ccnum = cc.ccnum;
        state.lynum = null;
        $hover = state;
    }

    function unhoverCC(cc) {
        if ($hover.ccnum === cc.ccnum) {
            $hover = {
                ccs: {},
                lys: {},
                ccnum: null,
                lynum: null,
                match: null,
            };
        }
    }

    function hoverLY(ly) {
        const state = _traverseLY({ ccs: {}, lys: {} }, ly);
        state.ccnum = null;
        state.lynum = ly.lynum;
        $hover = state;
    }

    function unhoverLY(ly) {
        if ($hover.lynum === ly.lynum) {
            $hover = {
                ccs: {},
                lys: {},
                ccnum: null,
                lynum: null,
                match: null,
            };
        }
    }

    // --- Selection state
    const selected = writable({
        ccs: {},
        lys: {},
    });
    const selectedCCs = derived(selected, ($selected) => {
        return Object.keys($selected.ccs);
    });
    const selectedLYs = derived(selected, ($selected) => {
        return Object.keys($selected.lys);
    });

    function toggleCCselection(cc) {
        if (cc.ccnum in $selected.ccs) {
            if (Object.keys($selected.ccs).length == 1) {
                $selected = { ccs: {}, lys: {} };
            } else {
                delete $selected.ccs[cc.ccnum];
                $selected = $selected;
            }
        } else {
            // add all connected cards
            $selected = _traverseCC($selected, cc);
        }
    }

    function toggleLYselection(ly) {
        if (ly.lynum in $selected.lys) {
            if (Object.keys($selected.lys).length == 1) {
                $selected = { ccs: {}, lys: {} };
            } else {
                delete $selected.lys[ly.lynum];
                $selected = $selected;
            }
        } else {
            // add all connected cards
            $selected = _traverseLY($selected, ly);
        }
    }

    function cardsOfMatch(match) {
        const state = _traverseCC(
            { ccs: {}, lys: {} },
            $creditcards[match.ccnum]
        );
        state.ccs = Object.keys(state.ccs);
        state.lys = Object.keys(state.lys);
        return state;
    }

    // --- Active states (not efficient but works...)
    const activeCC = derived(
        [creditcards, selected, hover],
        ([$creditcards, $selected, $hover]) => {
            return Object.keys($creditcards).reduce((map, ccnum) => {
                const some_selected = Object.keys($selected.ccs).length > 0;
                const this_selected = some_selected && ccnum in $selected.ccs;

                const some_hovered = Object.keys($hover.ccs).length > 0;
                const this_hovered = some_hovered && ccnum in $hover.ccs;

                map[ccnum] =
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered));
                return map;
            }, {});
        }
    );

    const activeLY = derived(
        [loyaltycards, selected, hover],
        ([$loyaltycards, $selected, $hover]) => {
            return Object.keys($loyaltycards).reduce((map, lynum) => {
                const some_selected = Object.keys($selected.lys).length > 0;
                const some_hovered = Object.keys($hover.lys).length > 0;

                const this_selected = some_selected && lynum in $selected.lys;
                const this_hovered = some_hovered && lynum in $hover.lys;

                map[lynum] =
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered));
                return map;
            }, {});
        }
    );

    const activeMatch = derived(
        [selected_matches, selected, hover],
        ([$selected_matches, $selected, $hover]) => {
            return $selected_matches.map((match) => {
                const some_selected = Object.keys($selected.lys).length > 0;
                const some_hovered = Object.keys($hover.lys).length > 0;

                const this_selected =
                    some_selected &&
                    match.lynum in $selected.lys &&
                    match.ccnum in $selected.ccs;
                const this_hovered =
                    some_hovered &&
                    match.lynum in $hover.lys &&
                    match.ccnum in $hover.ccs;

                return (
                    this_selected ||
                    (!some_selected && (this_hovered || !some_hovered))
                );
            });
        }
    );

    // --- Match transactions
    async function matchTransactions(dayNr, selectedCCs, selectedLYs) {
        const ccCursor = await db.query(aql`
            FOR c in raw_creditcard_transactions
                FILTER c.ccnum IN ${selectedCCs.map((v) => +v)}
                FILTER c.day == ${dayNr}
                LET lys = (
                    FOR l in raw_loyaltycard_transactions
                        FILTER l.day == (l.location == 'Kronos Mart' ? ${dayNr} - 1 : ${dayNr})
                        FILTER l.lynum IN ${selectedLYs}
                        FILTER l.month == c.month
                        FILTER l.price == c.price
                        FILTER l.location == c.location
                        RETURN l.lynum
                )
                RETURN {
                    ccnum: c.ccnum,
                    time_of_day: c.time_of_day,
                    display_time: c.display_time,
                    price: c.price,
                    location: c.location,
                    timestamp: c.timestamp,
                    lynums: lys
                }
        `);
        const lyCursor = await db.query(aql`
            FOR l in raw_loyaltycard_transactions
                FILTER l.lynum IN ${selectedLYs}
                FILTER l.day == (l.location == 'Kronos Mart' ? ${dayNr} - 1 : ${dayNr})
                LET ccs = (
                    FOR c in raw_creditcard_transactions
                        FILTER c.ccnum IN ${selectedCCs.map((c) => +c)}
                        FILTER l.month == c.month
                        FILTER c.day == ${dayNr}
                        FILTER l.price == c.price
                        FILTER l.location == c.location
                        RETURN c.ccnum
                )
                FILTER ccs == []
                SORT l.lynum 
                RETURN {
                    ccnum: '',
                    time_of_day: 0,
                    display_time: '00:00:00',
                    price: 0,
                    discount_price: l.price,
                    location: l.location,
                    timestamp: l.timestamp,
                    lynums: [l.lynum]
                }
        `);
        let ccValues = await ccCursor.all();
        let lyValues = await lyCursor.all();

        // Allow matches where ly price is not equal, but the location is the same.
        ccValues = ccValues.map((cc) => {
            if (cc.lynums.length === 0) {
                const half_matches = lyValues
                    .filter((ly) => ly.location === cc.location)
                    .sort(
                        (a, b) =>
                            Math.abs(b.price - cc.price) -
                            Math.abs(a.price - cc.price)
                    );
                if (half_matches.length > 0) {
                    lyValues = lyValues.filter((ly) => ly !== half_matches[0]);
                    cc.discount_price = half_matches[0].discount_price;
                    cc.lynums = half_matches[0].lynums;
                } else {
                    cc.discount_price = 0;
                }
            } else {
                cc.discount_price = cc.price;
            }
            return cc;
        });
        return { ccValues, lyValues };
    }

    // --- Setup context
    const key = {};
    setContext(key, {
        matches: selected_matches,
        activeMatch,
        hoverMatch,
        unhoverMatch,
        creditcards,
        orderCC,
        activeCC,
        hoverCC,
        unhoverCC,
        toggleCCselection,
        loyaltycards,
        orderLY,
        activeLY,
        hoverLY,
        unhoverLY,
        toggleLYselection,
        rejectedMatches,
        rejectMatch,
        restoreMatch,
        selected,
        selectedCCs,
        selectedLYs,
        matchTransactions,
        cardsOfMatch,
        corr_highpass,
        matches_highpass
    });
</script>

<slot {key} />
